/**
 * Wire-level types for the Kadanza Extension Page handshake.
 *
 * Field names match the example app — see
 * `kadanza-saas/extension-page-example/src/contexts/ExtensionContext.tsx`
 * and `EXTENSION_GUIDE.md` §10.
 *
 * UAT hosts may omit optional fields (`spaceId`, `pageId`, `designTokens`) and
 * send `authToken.expires` as a number; use `parseHandshakeAck` / `parseTokenRefresh`
 * to normalise host payloads before use.
 */

export const CONNECTION_EVENTS = {
  handshakeInit: 'HANDSHAKE_INIT',
  handshakeAck: 'HANDSHAKE_ACK',
  navigationChange: 'NAVIGATION_CHANGE',
  requestTokenRefresh: 'REQUEST_TOKEN_REFRESH',
  tokenRefresh: 'TOKEN_REFRESH',
} as const;

export type ConnectionEvent = (typeof CONNECTION_EVENTS)[keyof typeof CONNECTION_EVENTS];

export interface AuthToken {
  jwt: string;
  /** Unix timestamp (seconds since epoch) as a string. */
  expires: string;
}

export interface ExtensionDetails {
  extensionId: string;
  tenantId: string;
  tenantDomain: string;
  baseUrl: string;
  spaceId: string;
  pageId: string;
  locale: string;
}

export interface DesignTokens {
  primaryColor: string;
  fontFamily: string;
  /** ShadCN-friendly preset: `sm` | `md` | `lg` | `xl`. */
  borderRadius: string;
}

export interface HandshakeAckPayload {
  authToken: AuthToken;
  extensionDetails: ExtensionDetails;
  designTokens: DesignTokens;
}

export interface TokenRefreshPayload {
  authToken: AuthToken;
}

export type HostMessage =
  | { type: typeof CONNECTION_EVENTS.handshakeAck; payload: HandshakeAckPayload }
  | { type: typeof CONNECTION_EVENTS.tokenRefresh; payload: TokenRefreshPayload };

export type ExtensionMessage =
  | { type: typeof CONNECTION_EVENTS.handshakeInit }
  | { type: typeof CONNECTION_EVENTS.requestTokenRefresh }
  | {
      type: typeof CONNECTION_EVENTS.navigationChange;
      payload: { path: string };
    };

const DEFAULT_DESIGN_TOKENS: DesignTokens = {
  primaryColor: '#5b6bcc',
  fontFamily: 'Source Sans 3',
  borderRadius: 'md',
};

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Coerce host `expires` (string or number) to Unix-seconds string. */
export function normalizeExpires(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.floor(value));
  }
  const asString = asNonEmptyString(value);
  if (asString && /^\d+$/.test(asString)) return asString;
  return null;
}

/** Normalise a host auth token; falls back to jwt-only + 1h TTL when expires absent. */
export function normalizeAuthToken(raw: unknown): AuthToken | null {
  if (!raw || typeof raw !== 'object') return null;
  const token = raw as { jwt?: unknown; expires?: unknown };
  const jwt = asNonEmptyString(token.jwt);
  if (!jwt) return null;

  const expires =
    normalizeExpires(token.expires) ??
    String(Math.floor(Date.now() / 1000) + 3600);

  return { jwt, expires };
}

function normalizeDesignTokens(raw: unknown): DesignTokens {
  if (!raw || typeof raw !== 'object') return DEFAULT_DESIGN_TOKENS;
  const tokens = raw as Partial<DesignTokens>;
  return {
    primaryColor: asNonEmptyString(tokens.primaryColor) ?? DEFAULT_DESIGN_TOKENS.primaryColor,
    fontFamily: asNonEmptyString(tokens.fontFamily) ?? DEFAULT_DESIGN_TOKENS.fontFamily,
    borderRadius: asNonEmptyString(tokens.borderRadius) ?? DEFAULT_DESIGN_TOKENS.borderRadius,
  };
}

function normalizeExtensionDetails(raw: unknown): ExtensionDetails | null {
  if (!raw || typeof raw !== 'object') return null;
  const details = raw as Partial<ExtensionDetails>;
  const tenantId = asNonEmptyString(details.tenantId);
  if (!tenantId) return null;

  return {
    extensionId: asNonEmptyString(details.extensionId) ?? '',
    tenantId,
    tenantDomain: asNonEmptyString(details.tenantDomain) ?? '',
    baseUrl: asNonEmptyString(details.baseUrl) ?? '',
    spaceId: asNonEmptyString(details.spaceId) ?? '',
    pageId: asNonEmptyString(details.pageId) ?? '',
    locale: asNonEmptyString(details.locale) ?? 'en-US',
  };
}

/**
 * Parse and normalise a host `HANDSHAKE_ACK`. Returns null when the message is
 * not an ACK or lacks the minimum fields (`authToken.jwt`, `extensionDetails.tenantId`).
 */
export function parseHandshakeAck(
  msg: unknown,
): { type: typeof CONNECTION_EVENTS.handshakeAck; payload: HandshakeAckPayload } | null {
  if (!msg || typeof msg !== 'object') return null;
  const m = msg as { type?: unknown; payload?: unknown };
  if (m.type !== CONNECTION_EVENTS.handshakeAck) return null;

  const raw = m.payload;
  if (!raw || typeof raw !== 'object') return null;
  const partial = raw as Partial<HandshakeAckPayload>;

  const authToken = normalizeAuthToken(partial.authToken);
  const extensionDetails = normalizeExtensionDetails(partial.extensionDetails);
  if (!authToken || !extensionDetails) return null;

  return {
    type: CONNECTION_EVENTS.handshakeAck,
    payload: {
      authToken,
      extensionDetails,
      designTokens: normalizeDesignTokens(partial.designTokens),
    },
  };
}

/** Parse and normalise a host `TOKEN_REFRESH`. */
export function parseTokenRefresh(
  msg: unknown,
): { type: typeof CONNECTION_EVENTS.tokenRefresh; payload: TokenRefreshPayload } | null {
  if (!msg || typeof msg !== 'object') return null;
  const m = msg as { type?: unknown; payload?: unknown };
  if (m.type !== CONNECTION_EVENTS.tokenRefresh) return null;

  const raw = m.payload;
  if (!raw || typeof raw !== 'object') return null;
  const authToken = normalizeAuthToken((raw as Partial<TokenRefreshPayload>).authToken);
  if (!authToken) return null;

  return {
    type: CONNECTION_EVENTS.tokenRefresh,
    payload: { authToken },
  };
}

export function isHandshakeAck(
  msg: unknown,
): msg is { type: typeof CONNECTION_EVENTS.handshakeAck; payload: HandshakeAckPayload } {
  return parseHandshakeAck(msg) !== null;
}

/** Human-readable reasons when `parseHandshakeAck` returns null. */
export function describeHandshakeAckFailure(msg: unknown): string[] {
  const reasons: string[] = [];
  if (!msg || typeof msg !== 'object') {
    reasons.push('message is not an object');
    return reasons;
  }
  const m = msg as { type?: unknown; payload?: unknown };
  if (m.type !== CONNECTION_EVENTS.handshakeAck) {
    reasons.push(`type is ${String(m.type)} (expected ${CONNECTION_EVENTS.handshakeAck})`);
    return reasons;
  }
  const p = m.payload;
  if (!p || typeof p !== 'object') {
    reasons.push('payload is missing');
    return reasons;
  }
  const partial = p as Partial<HandshakeAckPayload>;
  if (!asNonEmptyString((partial.authToken as { jwt?: unknown } | undefined)?.jwt)) {
    reasons.push('payload.authToken.jwt is missing or not a string');
  } else if (
    normalizeExpires((partial.authToken as { expires?: unknown } | undefined)?.expires) == null
  ) {
    reasons.push(
      'payload.authToken.expires is missing or not coercible (number or numeric string expected)',
    );
  }
  if (!asNonEmptyString(partial.extensionDetails?.tenantId)) {
    reasons.push('payload.extensionDetails.tenantId is missing or not a string');
  }
  return reasons;
}

export function isTokenRefresh(
  msg: unknown,
): msg is { type: typeof CONNECTION_EVENTS.tokenRefresh; payload: TokenRefreshPayload } {
  return parseTokenRefresh(msg) !== null;
}
